import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatPhoneInput } from "../../utils/phoneUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { Virtuoso, TableVirtuoso } from "react-virtuoso";

const CUSTOMERS_VIRTUALIZATION_THRESHOLD = 100;
const CUSTOMERS_PAGE_SIZE = 50;

// Shadcn <TableBody> wrapped so react-virtuoso can attach its ref + scroll-state props.
const VirtualTableBody = React.forwardRef(function VirtualTableBody(props, ref) {
  return <TableBody {...props} ref={ref} />;
});
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, User, Send, Loader2, Filter, X, Download, CalendarPlus } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { useTheme } from "../../contexts/ThemeContext";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import CustomerModal from "../../components/customers/CustomerModal";
import JobPreviewModal from "../../components/jobs/JobPreviewModal";

const CustomersPage = () => {
  const { isDark } = useTheme();
  const { isSmsReadyAsync } = usePlanLimits();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  // Server-side pagination state — cursor + total from response headers.
  // Stays O(log n) at any depth because we use keyset (cursor) instead of skip.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const fetchSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const nextCursorRef = useRef("");
  const [invitingCustomer, setInvitingCustomer] = useState(null);
  // Unified modal — opens for create / view / edit. `modalCustomer === null`
  // = create mode; otherwise the modal hydrates from this object.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);

  // ─────────────────────────────────────────────────────────────────────
  // Server-side fetching. The full Customers list used to be slurped on
  // mount and filtered client-side; this melts down at 5k+ rows. Now the
  // API does the heavy lifting (search + sort + paging) against compound
  // indexes on `(user_id, created_at desc, customer_id desc)`,
  // `(user_id, name)`, `(user_id, phone)`, `(user_id, email)`. Page 1 and
  // page 2000 cost the same because we use keyset cursor pagination.
  // ─────────────────────────────────────────────────────────────────────
  const buildCustomersQuery = useCallback((cursorValue = "") => {
    const p = new URLSearchParams();
    p.set("limit", String(CUSTOMERS_PAGE_SIZE));
    if (cursorValue) p.set("cursor", cursorValue);
    if (searchQuery && searchQuery.trim().length >= 2) p.set("search", searchQuery.trim());
    return p;
  }, [searchQuery]);

  const fetchCustomers = useCallback(async ({ cursor = "", append = false } = {}) => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    const seq = ++fetchSeqRef.current;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const params = buildCustomersQuery(cursor);
      const res = await axios.get(`${API}/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (seq !== fetchSeqRef.current) return;
      const data = Array.isArray(res.data) ? res.data : [];
      setCustomers(prev => append ? [...prev, ...data] : data);
      nextCursorRef.current = res.headers?.["x-next-cursor"] || "";
      setHasMore(Boolean(nextCursorRef.current));
      const tc = parseInt(res.headers?.["x-total-count"] || "0", 10);
      if (Number.isFinite(tc)) setTotal(tc);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Failed to fetch customers:", error);
        if (!append) toast.error("Failed to load customers");
      }
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildCustomersQuery]);

  // Refetch on search-query change with a 300ms debounce so we don't fire
  // a request on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = searchQuery && searchQuery.trim().length >= 2 ? 300 : 0;
    debounceRef.current = setTimeout(() => {
      nextCursorRef.current = "";
      fetchCustomers({ cursor: "", append: false });
    }, delay);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchCustomers, searchQuery]);

  const loadMoreCustomers = useCallback(() => {
    if (loadingMore || !hasMore || !nextCursorRef.current) return;
    fetchCustomers({ cursor: nextCursorRef.current, append: true });
  }, [loadingMore, hasMore, fetchCustomers]);

  // ───────────────────────────────────────────────────────────────
  // All create / edit logic now lives inside <CustomerModal/>. The page
  // just hands the modal a customer (or null for create) and listens
  // for `onSaved` / `onDeleted` to refresh the list.
  // ───────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setModalCustomer(null);
    setModalOpen(true);
  };

  const openCustomerModal = (customer) => {
    setModalCustomer(customer);
    setModalOpen(true);
  };

  // Deep link: /dashboard/customers?open=<customer_id> (from global search)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/customers/${openId}`, authCfg());
        openCustomerModal(res.data);
      } catch {
        toast.error("Customer not found");
      } finally {
        setSearchParams({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleModalSaved = () => {
    fetchCustomers();
  };

  const handleModalDeleted = () => {
    fetchCustomers();
  };

  // ── Cross-navigation: Customer profile ↔ Job preview ──────────────
  const navigate = useNavigate();
  const [previewJob, setPreviewJob] = useState(null);
  const [sendingToTech, setSendingToTech] = useState(null);
  const [sendingReview, setSendingReview] = useState(null);

  const authCfg = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const openJobPreview = async (job) => {
    if (!job?.job_id) return;
    setModalOpen(false);
    try {
      const res = await axios.get(`${API}/jobs/${job.job_id}`, authCfg());
      setPreviewJob(res.data);
    } catch {
      setPreviewJob(job);
    }
  };

  // Closing the job preview returns the user to the customer profile.
  const backToCustomerModal = () => {
    setPreviewJob(null);
    setModalOpen(true);
  };

  const handleJobEdit = (job) => {
    setPreviewJob(null);
    navigate(`/dashboard/jobs?job=${job.job_id}`);
  };

  const handleJobDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title || "this job"}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${job.job_id}`, authCfg());
      toast.success("Job deleted");
    } catch {
      toast.error("Failed to delete job");
    }
  };

  const handleJobReminder = async (job) => {
    if (!(await isSmsReadyAsync())) return;
    try {
      await axios.post(`${API}/notifications/send-reminder`, {
        job_id: job.job_id,
        customer_phone: job.customer_phone,
        customer_name: job.customer_name,
        job_title: job.title,
        scheduled_date: job.scheduled_date,
        scheduled_time: job.scheduled_time,
        address: job.customer_address,
      }, authCfg());
      toast.success("Reminder sent successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleJobSendToTech = async (job) => {
    if (!job.assigned_to) { toast.error("No technician assigned to this job"); return; }
    if (!(await isSmsReadyAsync())) return;
    setSendingToTech(job.job_id);
    try {
      const res = await axios.post(`${API}/technicians/jobs/${job.job_id}/send-assignment`, {}, authCfg());
      if (res.data?.masking_warning) toast.warning(res.data.masking_warning, { duration: 9000 });
      toast.success("Job details sent to technician");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send to technician");
    } finally {
      setSendingToTech(null);
    }
  };

  const handleJobReviewRequest = async (job) => {
    if (!(await isSmsReadyAsync())) return;
    setSendingReview(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/send-review-request`, {}, authCfg());
      toast.success("Review request sent to customer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send review request");
    } finally {
      setSendingReview(null);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/customers/${customerId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Customer deleted successfully");
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/customers/export/csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `customers_${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Customer list exported");
    } catch (error) {
      toast.error("Failed to export customers");
    } finally {
      setExporting(false);
    }
  };

  const handleInviteToPortal = async (customer) => {
    if (!customer.phone && !customer.email) {
      toast.error("Customer needs a phone number or email to receive an invite");
      return;
    }
    // If sending SMS, check phone number is configured
    if (customer.phone && !(await isSmsReadyAsync())) return;
    setInvitingCustomer(customer.customer_id);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/portal/invite`, {
        customer_id: customer.customer_id,
        send_sms: !!customer.phone,
        send_email: !!customer.email,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      
      const data = res.data;
      if (data.already_registered) {
        toast.info(`${customer.name} already has portal access`);
      } else if (data.sms_sent || data.email_sent) {
        const channels = [data.sms_sent && "SMS", data.email_sent && "email"].filter(Boolean).join(" & ");
        toast.success(`Portal invite sent to ${customer.name} via ${channels}`);
      } else {
        const errorMsg = data.message || data.sms_error || data.email_error || "No invite channels available. Ensure the customer has a phone number or email address.";
        toast.warning(errorMsg);
      }
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send portal invite");
    } finally {
      setInvitingCustomer(null);
    }
  };

  const handleEdit = (customer) => openCustomerModal(customer);
  const handleCustomerClick = (customer) => openCustomerModal(customer);

  // Server already applied the cross-field search via `?search=` so the
  // common case is a no-op. When the user narrows to a specific field
  // (name / phone / email / address) we refine client-side over the
  // already-narrow server result — keeps the existing per-field UX without
  // adding a `field` param to the API.
  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery || searchField === "all") return true;
    const q = searchQuery.toLowerCase();
    if (searchField === "name") return customer.name.toLowerCase().includes(q);
    if (searchField === "phone") return customer.phone.includes(q);
    if (searchField === "email") return customer.email && customer.email.toLowerCase().includes(q);
    if (searchField === "address") return (customer.address || "").toLowerCase().includes(q) || (customer.city || "").toLowerCase().includes(q);
    return true;
  });

  // Cell renderer reused by both the regular table and the virtualized table.
  // Returns ONLY the <TableCell> fragments — caller wraps in <TableRow>.
  // Full one-line address: "123 Main St, Austin, TX, 78701"
  const fullAddress = (c) => [c.address, c.city, c.state, c.zip_code].filter(Boolean).join(", ");

  // Quick action — open the job form prefilled with this customer.
  const handleCreateJob = (customer) => {
    navigate("/dashboard/jobs/new", {
      state: {
        customerPrefill: {
          customer_id: customer.customer_id || "",
          customer_name: customer.name || "",
          customer_phone: customer.phone || "",
          customer_email: customer.email || "",
          customer_address: customer.address || "",
          address_city: customer.city || "",
          address_state: customer.state || "",
          zip_code: customer.zip_code || "",
        },
      },
    });
  };

  const renderCustomerRowCells = (customer, index) => (    <>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0046BE]/20 flex items-center justify-center">
            <User className="w-5 h-5 text-[#0046BE]" />
          </div>
          <div>
            <p className="font-medium text-foreground">{customer.name}</p>
            <p className="text-xs text-muted-foreground">
              Added {format(new Date(customer.created_at), "MMM d, yyyy")}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <p className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-3 h-3" /> {customer.phone}
          </p>
          {customer.email && (
            <p className="flex items-center gap-2 text-muted-foreground text-sm">
              <Mail className="w-3 h-3" /> {customer.email}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge className={`${getServiceBadge(customer.service_type)} border capitalize`}>
          {customer.service_type.replace("_", " ")}
        </Badge>
      </TableCell>
      <TableCell>
        {fullAddress(customer) ? (
          <p className="flex items-center gap-2 text-muted-foreground max-w-[260px]" title={fullAddress(customer)}>
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate" data-testid={`customer-address-${index}`}>{fullAddress(customer)}</span>
          </p>
        ) : (
          <span className={isDark ? 'text-white/30' : 'text-gray-300'}>-</span>
        )}
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <p className="text-muted-foreground">{customer.total_jobs} jobs</p>
          <p className="text-xs text-green-400">${customer.total_spent.toLocaleString()}</p>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCreateJob(customer)}
            className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
            title="Create Job"
            data-testid={`create-job-${index}`}
          >
            <CalendarPlus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleInviteToPortal(customer)}
            disabled={invitingCustomer === customer.customer_id}
            className={`${isDark ? 'text-[#0046BE] hover:text-[#0046BE] hover:bg-[#0046BE]/10' : 'text-[#0046BE] hover:text-[#0046BE] hover:bg-[#0046BE]/10'}`}
            title="Invite to Customer Portal"
            data-testid={`invite-portal-${index}`}
          >
            {invitingCustomer === customer.customer_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(customer)}
            className={`${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            data-testid={`edit-customer-${index}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(customer.customer_id)}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid={`delete-customer-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </>
  );

  // Card renderer reused by both the regular mobile list and the virtualized one.
  const renderCustomerCard = (customer, index) => (
    <Card key={customer.customer_id} className="glass-card border-theme cursor-pointer" data-testid={`customer-card-${index}`} onClick={() => handleCustomerClick(customer)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full bg-[#0046BE]/20 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-[#0046BE]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground truncate">{customer.name}</p>
              <Badge className={`${getServiceBadge(customer.service_type)} border capitalize text-xs mt-1`}>
                {customer.service_type.replace("_", " ")}
              </Badge>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleInviteToPortal(customer)}
              disabled={invitingCustomer === customer.customer_id}
              className="h-8 w-8 text-[#0046BE] hover:bg-[#0046BE]/10"
              title="Invite to Portal"
            >
              {invitingCustomer === customer.customer_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEdit(customer)}
              className={`h-8 w-8 ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(customer.customer_id)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-muted-foreground active:text-[#FFCC00]">
            <Phone className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{customer.phone}</span>
          </a>
          {customer.email && (
            <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-muted-foreground truncate active:text-[#FFCC00]">
              <Mail className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{customer.email}</span>
            </a>
          )}
          {(customer.city || customer.address) && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{fullAddress(customer)}</span>
            </p>
          )}
        </div>

        <div className={`flex items-center justify-between mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <span className="text-muted-foreground text-xs">
            {format(new Date(customer.created_at), "MMM d, yyyy")}
          </span>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{customer.total_jobs} jobs</span>
            <span className="text-green-400 font-medium">${customer.total_spent.toLocaleString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const useVirtualization = filteredCustomers.length > CUSTOMERS_VIRTUALIZATION_THRESHOLD || hasMore;
  const headerRowClasses = `hover:bg-transparent ${isDark ? 'border-white/10' : 'border-gray-200'}`;
  const dataRowClasses = `cursor-pointer ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`;

  // Custom TableRow used by TableVirtuoso so each row carries the customer click handler.
  const VirtualCustomerRow = ({ item, ...rest }) => (
    <TableRow
      {...rest}
      className={dataRowClasses}
      data-testid={`customer-row-${rest['data-index']}`}
      onClick={() => handleCustomerClick(item)}
    />
  );

  const getServiceBadge = (type) => {
    const colors = {
      locksmith: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      garage_door: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      carpet_cleaning: "bg-green-500/20 text-green-400 border-green-500/30",
      other: "bg-purple-500/20 text-purple-400 border-purple-500/30"
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Customers</h2>
          <p className="text-muted-foreground">Manage your customer database</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-theme"
            data-testid="export-customers-btn"
            disabled={exporting}
            onClick={handleExportCsv}
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button className="btn-primary" data-testid="add-customer-btn" onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" /> Add Customer
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
          <Input
            placeholder={searchField === "all" ? "Search by name, phone, email, address..." : `Search by ${searchField}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-glass pl-10 pr-8"
            data-testid="customer-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={searchField} onValueChange={setSearchField}>
          <SelectTrigger className="input-glass w-full sm:w-[140px]" data-testid="search-filter-select">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Fields</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="phone">Phone</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="address">Address</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Customers Table - Desktop */}
      <Card className="glass-card border-theme hidden md:block" data-testid="customers-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading customers...</div>
          ) : filteredCustomers.length > 0 ? (
            useVirtualization ? (
              <TableVirtuoso
                useWindowScroll
                data={filteredCustomers}
                increaseViewportBy={{ top: 400, bottom: 800 }}
                endReached={loadMoreCustomers}
                components={{
                  Table: ({ style, ...rest }) => <Table {...rest} style={{ ...style, tableLayout: "fixed" }} />,
                  TableHead: TableHeader,
                  TableRow: VirtualCustomerRow,
                  TableBody: VirtualTableBody,
                  TableFoot: () => loadingMore ? (
                    <tr><td colSpan={6} className="text-center py-3 text-xs text-muted-foreground">Loading more customers…</td></tr>
                  ) : null,
                }}
                fixedHeaderContent={() => (
                  <TableRow className={headerRowClasses}>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Contact</TableHead>
                    <TableHead className="text-muted-foreground">Service</TableHead>
                    <TableHead className="text-muted-foreground">Location</TableHead>
                    <TableHead className="text-muted-foreground">Stats</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                )}
                itemContent={(index, customer) => renderCustomerRowCells(customer, index)}
                data-testid="customers-virtuoso"
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className={headerRowClasses}>
                    <TableHead className="text-muted-foreground">Customer</TableHead>
                    <TableHead className="text-muted-foreground">Contact</TableHead>
                    <TableHead className="text-muted-foreground">Service</TableHead>
                    <TableHead className="text-muted-foreground">Location</TableHead>
                    <TableHead className="text-muted-foreground">Stats</TableHead>
                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer, index) => (
                    <TableRow key={customer.customer_id} className={dataRowClasses} data-testid={`customer-row-${index}`} onClick={() => handleCustomerClick(customer)}>
                      {renderCustomerRowCells(customer, index)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <div className="text-center py-12">
              <User className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
              <p className="text-muted-foreground">
                {searchQuery ? "No customers found" : "No customers yet"}
              </p>
              {!searchQuery && (
                <Button className="mt-4 btn-primary" onClick={openCreateModal} data-testid="empty-add-customer-btn">
                  Add Your First Customer
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customers Cards - Mobile */}
      <div className="md:hidden" data-testid="customers-mobile-cards">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading customers...</div>
        ) : filteredCustomers.length > 0 ? (
          useVirtualization ? (
            <Virtuoso
              useWindowScroll
              data={filteredCustomers}
              increaseViewportBy={{ top: 400, bottom: 800 }}
              endReached={loadMoreCustomers}
              itemContent={(index, customer) => (
                <div className="pb-3">{renderCustomerCard(customer, index)}</div>
              )}
              components={{
                Footer: () => loadingMore ? (
                  <div className="text-center py-3 text-xs text-muted-foreground">Loading more customers…</div>
                ) : null,
              }}
              data-testid="customers-mobile-virtuoso"
            />
          ) : (
            <div className="space-y-3">
              {filteredCustomers.map((customer, index) => renderCustomerCard(customer, index))}
            </div>
          )
        ) : (
          <Card className="glass-card border-theme">
            <CardContent className="text-center py-12">
              <User className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
              <p className="text-muted-foreground">
                {searchQuery ? "No customers found" : "No customers yet"}
              </p>
              {!searchQuery && (
                <Button className="mt-4 btn-primary" onClick={openCreateModal}>
                  Add Your First Customer
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Unified Customer Modal — handles create, view, and edit */}
      <CustomerModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        customer={modalCustomer}
        onSaved={handleModalSaved}
        onDeleted={handleModalDeleted}
        onOpenJob={openJobPreview}
        onCreateJob={handleCreateJob}
      />

      {/* Job preview opened from the customer profile (Jobs / Files tab) */}
      {previewJob && (
        <JobPreviewModal
          job={previewJob}
          onClose={backToCustomerModal}
          onEdit={handleJobEdit}
          onDelete={handleJobDelete}
          onSendReminder={handleJobReminder}
          onSendToTech={handleJobSendToTech}
          onSendReviewRequest={handleJobReviewRequest}
          onOpenCustomer={backToCustomerModal}
          sendingToTech={sendingToTech}
          sendingReview={sendingReview}
        />
      )}
    </div>
  );
};

export default CustomersPage;
